import { Check, X, XCircle, Pencil } from 'lucide-react';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';

interface ResultPhaseProps {
    variant?: 'light' | 'dark';
    success: boolean;
    failure: boolean;
    statusMessage?: string;
    onClose: () => void;
    eventId?: string;
    onCancel?: () => void;
    onReschedule?: () => void;
    showSpamHint?: boolean;
    /**
     * Heading shown on success. Defaults to the booking wording; the
     * request-mode card overrides it, because nothing has been booked. See
     * BC-5174.
     */
    successTitle?: string;
    /** Heading shown on failure. Defaults to the booking wording. */
    failureTitle?: string;
    /**
     * Let the light result fill its container instead of the fixed size the
     * booking modal needs. The inline accordion card is much narrower, so the
     * fixed width breaks out of it. Off by default, so the modal is unchanged.
     * See BC-5174.
     */
    fluid?: boolean;
}

/**
 * Displays the result of a booking attempt (success or failure). Supports
 * `variant="dark"` for the revamped project detail page's inline dark booking card.
 */
export const ResultPhase = ({
    variant = 'light',
    success,
    failure,
    statusMessage,
    onClose,
    eventId,
    onCancel,
    onReschedule,
    showSpamHint = false,
    successTitle = 'Meeting booked',
    failureTitle = 'Booking failed',
    fluid = false,
}: ResultPhaseProps) => {
    const shellClass = fluid
        ? 'flex-stretch flex w-full items-center justify-center overflow-hidden rounded-3 bg-[#fafafa] px-12 py-12'
        : 'flex-stretch flex h-170 w-358 items-center justify-center overflow-hidden rounded-3 bg-[#fafafa] px-12 pt-12';
    const innerClass = fluid
        ? 'relative mx-auto flex w-full items-center justify-center text-center'
        : 'relative -top-22 mx-auto flex h-140 w-full items-center justify-center text-center';
    if (variant === 'dark') {
        if (success) {
            return (
                <div
                    className="flex flex-col items-center py-[2.4rem] text-center text-white"
                    role="alert"
                    aria-label="Booking successful"
                >
                    <Check className="text-green-400 mb-[1.2rem] h-[2.4rem] w-[2.4rem]" />
                    <h2 className="text-[1.248rem] leading-tight font-semibold">
                        {successTitle}
                    </h2>
                    <p className="mt-[0.48rem] max-w-[25rem] text-[0.864rem] whitespace-pre-wrap text-white/70">
                        {statusMessage || ''}
                    </p>
                    {showSpamHint ? (
                        <p className="text-amber-400 mt-[1.44rem] text-[0.72rem]">
                            <span className="font-semibold">
                                Didn't receive a calendar invite?
                            </span>{' '}
                            Please check your spam or junk folder.
                        </p>
                    ) : null}
                    {eventId && (onCancel || onReschedule) ? (
                        <div className="mt-[1.44rem] flex gap-[1.44rem]">
                            {onCancel ? (
                                <button
                                    onClick={onCancel}
                                    className="flex items-center gap-1 text-[0.768rem] font-medium text-white/70 transition-colors hover:text-white"
                                    aria-label="Cancel meeting"
                                >
                                    <XCircle className="h-[0.864rem] w-[0.864rem]" />
                                    Cancel Meeting
                                </button>
                            ) : null}
                            {onReschedule ? (
                                <button
                                    onClick={onReschedule}
                                    className="flex items-center gap-1 text-[0.768rem] font-medium text-white/70 transition-colors hover:text-white"
                                    aria-label="Reschedule meeting"
                                >
                                    <Pencil className="h-[0.768rem] w-[0.768rem]" />
                                    Reschedule Meeting
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <Button
                        onClick={onClose}
                        color="white"
                        className="mt-[1.92rem] h-[2.496rem] rounded-[var(--radius-btn-m)] px-[1.92rem] text-[0.768rem]"
                    >
                        Close
                    </Button>
                </div>
            );
        }

        if (failure) {
            return (
                <div
                    className="flex flex-col items-center py-[2.4rem] text-center text-white"
                    role="alert"
                    aria-label="Booking failed"
                >
                    <X className="mb-[1.2rem] h-[2rem] w-[2rem] text-red-400" />
                    <h2 className="text-[1.248rem] leading-tight font-semibold">
                        {failureTitle}
                    </h2>
                    <p className="mt-[0.48rem] max-w-[25rem] text-[0.864rem] whitespace-pre-wrap text-white/70">
                        {statusMessage ||
                            'Something went wrong while booking your meeting. Please try again or contact support if the problem persists.'}
                    </p>
                    <Button
                        onClick={onClose}
                        color="white"
                        className="mt-[1.92rem] h-[2.496rem] rounded-[var(--radius-btn-m)] px-[1.92rem] text-[0.768rem]"
                    >
                        Close
                    </Button>
                </div>
            );
        }

        return null;
    }

    // Light variant (default) — used in the booking modal
    const successContent = (
        <div
            className={shellClass}
            style={{ display: success ? 'flex' : 'none' }}
            role="alert"
            aria-label="Booking successful"
        >
            <div className={innerClass}>
                <div>
                    <Check className="text-green-600 mx-auto mb-5 inline-block h-16 w-16" />
                    <h2 className="text-7 leading-tight font-semibold text-[#161717]">
                        {successTitle}
                    </h2>
                    <p
                        className={cn(
                            'mt-2 line-clamp-6 whitespace-pre-wrap',
                            fluid ? 'mx-auto max-w-100' : 'w-100',
                        )}
                    >
                        {statusMessage || ''}
                    </p>
                    {showSpamHint && (
                        <div className="mt-7 text-sm">
                            <p className="font-semibold">
                                Didn't Receive a calendar invite?
                            </p>
                            <p>Please check your spam or junk folder.</p>
                        </div>
                    )}
                </div>

                <div className="absolute -bottom-4 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3">
                    <Button onClick={onClose} className="h-11 w-39">
                        Close
                    </Button>
                    {eventId && (onCancel || onReschedule) && (
                        <div className="mt-2 flex w-100 items-center justify-center gap-4">
                            {onCancel && (
                                <button
                                    onClick={onCancel}
                                    className="flex items-center gap-1 text-sm font-medium text-[#161717] transition-colors hover:text-brick-600"
                                    aria-label="Cancel meeting"
                                >
                                    <XCircle className="h-4 w-4" />
                                    Cancel Meeting
                                </button>
                            )}
                            {onReschedule && (
                                <button
                                    onClick={onReschedule}
                                    className="flex items-center gap-1 text-sm font-medium text-[#161717] transition-colors hover:text-brick-600"
                                    aria-label="Reschedule meeting"
                                >
                                    <Pencil className="h-4 w-4" />
                                    Reschedule Meeting
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const failureContent = (
        <div
            className={shellClass}
            style={{ display: failure ? 'flex' : 'none' }}
            role="alert"
            aria-label="Booking failed"
        >
            <div
                className={
                    fluid
                        ? 'relative mx-auto flex w-full items-center justify-center px-12 text-center'
                        : 'relative -top-22 mx-auto flex h-140 w-full items-center justify-center p-12 px-12 text-center'
                }
            >
                <div>
                    <X className="mx-auto mb-5 inline-block h-8.5 w-8.5 text-red-600" />
                    <h2 className="text-7 leading-tight font-semibold text-[#161717]">
                        {failureTitle}
                    </h2>
                    <p className="mt-2 whitespace-pre-wrap">
                        {statusMessage ||
                            'Something went wrong while booking your meeting. Please try again or contact support if the problem persists.'}
                    </p>
                </div>

                <Button
                    onClick={onClose}
                    className="absolute bottom-11 left-1/2 h-11 w-39 -translate-x-1/2"
                >
                    Close
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {successContent}
            {failureContent}
        </>
    );
};
